import {undo} from './ContentEditorActionCreators';
import {Dispatch} from '@packages/systems/flux';
import type {LocaleItemContext} from '@packages/systems/plugins/Localization';
import {type PluginsContext} from '@packages/systems/wfdl';
import type {InlineMarkdownType} from '@packages/shared/types/ContentEditor';

export const isActiveLocalePrimary = (pluginsContext: PluginsContext) => {
  // If there's no active locale, assume that the site doesn't have Localization set up
  // and treat this as the primary locale.
  const locales = pluginsContext.get('Localization')?.get('locales');
  const activeLocale = locales.find(
    (locale: LocaleItemContext) => locale.active
  );

  if (!activeLocale) {
    return true;
  }

  return activeLocale.primary;
};

export const undoIfErrorCaught = (fn: any, dispatch: Dispatch) => {
  return (...args: Array<any>) => {
    try {
      return fn(...args);
    } catch (e: any) {
      // TEMP: Disable bug reporting until we need new sample data
      // TEMP: Make it console.log in prod so users can report issues if needed
      // reportBug(
      //   'ContentEditorHelperError',
      //   'Error caught in native interface',
      //   {
      //     error: e.message,
      //     stack: e.stack,
      //   }
      // );

      // if (process.env.NODE_ENV === 'development') {
      console.error(
        'There was an error in the native ContentEditor render interface',
        e
      );
      // }

      dispatch(undo());
    }
  };
};

// Returns the type of (supported) inline markdown syntax the text provided has
export const getInlineMarkdownTypeInText = (
  text: string
): InlineMarkdownType | null => {
  const boldPattern = /((^|[^*])\*\*|(^|[^_])__)([^*_]+)(\*\*(?!\*)|__(?!_))/g;
  const emphasisPattern = /((^|[^*])\*|(^|[^_])_)([^*_]+)(\*(?!\*)|_(?!_))/g;
  const boldAndEmphasisPattern = /(\*\*\*|___)([^*_]+)(\*\*\*|___)/g;
  const linkPattern = /(\[.*\])(\((http)(?:s)?(\:\/\/).*\))/g;
  const inlineCodePattern = /(^|[^`])`([^`]+)([^`]+)`/g;

  const boldMatches = text.match(boldPattern);
  const emphasisMatches = text.match(emphasisPattern);
  const boldAndEmphasisMatches = text.match(boldAndEmphasisPattern);
  const linkMatches = text.match(linkPattern);
  const inlineCodeMatches = text.match(inlineCodePattern);

  // Returns code first to ensure that, if we have markdown syntax nested inside the
  // code backticks, we turn it into code and not bold/italic.
  if (inlineCodeMatches) {
    return 'code';
  } else if (boldMatches || emphasisMatches || boldAndEmphasisMatches) {
    return 'bold italic';
  } else if (linkMatches) {
    return 'link';
  } else {
    return null;
  }
};

// Returns whether the text provided has (supported) block markdown syntax
export const hasBlockMarkdownInText = (text: string): boolean => {
  const headerPattern = /^#{1,6}\s/g;
  const blockquotePattern = /^>\s/g;
  const ulPattern = /^-\s/g;
  const olPattern = /^\d+\.\s/g;

  const headerMatches = text.match(headerPattern);
  const blockquoteMatches = text.match(blockquotePattern);
  const ulMatches = text.match(ulPattern);
  const olMatches = text.match(olPattern);

  return Boolean(headerMatches || blockquoteMatches || ulMatches || olMatches);
};
